import React, { useEffect, useState } from "react";
import apiClient from "../../services/apiClient";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Paper,
} from "@mui/material";
import { MdPayment } from "react-icons/md";
import CustomButton from "../Button/CustomButton";

export default function Payment() {
  const [country, setCountry] = useState("IN");
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);

  const userId = useSelector((state) => state.userInfo?.user?.id);

  // -------------------------------------------------------------------------------------
  // LOAD RAZORPAY SCRIPT INSIDE REACT (REQUIRED FOR REACT 18)
  // -------------------------------------------------------------------------------------
  useEffect(() => {
    const loadScript = () => {
      return new Promise((resolve) => {
        const script = document.createElement("script");
        script.src = "https://checkout.razorpay.com/v1/checkout.js";
        script.onload = () => resolve(true);
        script.onerror = () => resolve(false);
        document.body.appendChild(script);
      });
    };

    loadScript().then((loaded) => {
      if (!loaded) {
        toast.error("Failed to load Razorpay SDK");
      }
    });
  }, []);

  // -------------------------------------------------------------------------------------
  // PAY NOW FUNCTION
  // -------------------------------------------------------------------------------------
  const payNow = async () => {
    if (!amount || Number(amount) <= 0) {
      toast.error("Enter a valid amount");
      return;
    }

    if (!userId) {
      toast.error("User not logged in");
      return;
    }

    try {
      setLoading(true);

      // 1️⃣ CREATE ORDER
      const json = await apiClient.post("/payment/create", {
        user_id: userId,
        country,
        amount: Number(amount),
      });

      console.log("Create Order Response:", json);

      if (!json.status) {
        toast.error(json.message || "Failed to create order");
        setLoading(false);
        return;
      }

      const order = json.data;

      // Make sure Razorpay has loaded
      if (!window.Razorpay) {
        toast.error("Razorpay SDK not loaded yet");
        setLoading(false);
        return;
      }

      // 2️⃣ BUILD PAYMENT OPTIONS
      const options = {
        key: order.key,
        amount: order.amount,
        currency: order.currency,
        name: "Dial Grid",
        description: "Wallet Top-up",
        order_id: order.order_id,

        handler: async function (response) {
          console.log("Razorpay Response:", response);

          if (!response.razorpay_payment_id) {
            toast.error("Payment failed or cancelled");
            setLoading(false);
            return;
          }

          try {
            const verifyResponse = await apiClient.post("/payment/verify", {
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature,
              db_id: order.db_id,
            });

            console.log("Verify Response:", verifyResponse);

            if (verifyResponse.status) {
              toast.success("Payment Successful");
              setAmount("");
            } else {
              toast.error(verifyResponse.message || "Payment verification failed");
            }
          } catch (err) {
            console.error("Verification Error:", err);
            toast.error(err.response?.data?.message || "Payment verification failed");
          } finally {
            setLoading(false);
          }
        },

        modal: {
          ondismiss: function () {
            console.log("Payment popup closed");
            toast.error("Payment cancelled");
            setLoading(false);
          },
        },

        prefill: {
          email: "user@example.com",
          contact: "9999999999",
        },

        theme: { color: "#3399cc" },
      };

      // 3️⃣ OPEN RAZORPAY POPUP
      const rzp = new window.Razorpay(options);

      rzp.on("payment.failed", function (response) {
        console.error("Payment Failed:", response.error);
        toast.error(response.error.description || "Payment failed");
        setLoading(false);
      });

      rzp.open();

    } catch (err) {
      console.error("Payment Error:", err);
      toast.error(err.response?.data?.message || "Something went wrong");
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 90px)",
        width: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        background: "#F9FAFB",
        p: 3,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: "450px",
          borderRadius: "12px",
          overflow: "hidden",
          border: "1px solid #E5E7EB",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            p: 3,
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "10px",
              backgroundColor: "rgba(255, 255, 255, 0.2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <MdPayment style={{ color: "white", fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ color: "white", fontWeight: 600, mb: 0.5 }}>
              Make Payment
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(255, 255, 255, 0.9)" }}>
              Top-up your wallet securely
            </Typography>
          </Box>
        </Box>

        {/* Body */}
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {/* Country Select */}
            <Box>
              <Typography
                variant="body2"
                sx={{ mb: 1, fontWeight: 600, color: "#374151" }}
              >
                Select Country
              </Typography>
              <TextField
                select
                fullWidth
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                  },
                }}
              >
                <MenuItem value="IN">India</MenuItem>
                <MenuItem value="US">United States</MenuItem>
              </TextField>
            </Box>

            {/* Amount Input */}
            <Box>
              <Typography
                variant="body2"
                sx={{ mb: 1, fontWeight: 600, color: "#374151" }}
              >
                Enter Amount
              </Typography>
              <TextField
                fullWidth
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                size="small"
                slotProps={{
                  htmlInput: { min: 0 },
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                  },
                }}
              />
            </Box>

            {/* Info Box */}
            <Box
              sx={{
                p: 2,
                backgroundColor: "#F0F9FF",
                borderRadius: "8px",
                border: "1px solid #BAE6FD",
                display: "flex",
                gap: 1.5,
              }}
            >
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  backgroundColor: "#0EA5E9",
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "14px",
                  fontWeight: "bold",
                  flexShrink: 0,
                }}
              >
                i
              </Box>
              <Typography
                variant="body2"
                sx={{ color: "#0369A1", fontSize: "0.875rem", lineHeight: 1.6 }}
              >
                Your payment is secure and encrypted. Funds will be added to your wallet
                instantly after successful payment.
              </Typography>
            </Box>

            {/* Pay Button */}
            <CustomButton
              onClick={payNow}
              disabled={loading}
              loading={loading}
              variant="contained"
              fullWidth
              sx={{
                mt: 1,
                borderRadius: "8px",
                backgroundColor: "#667eea",
                color: "#FFFFFF",
                padding: "12px 16px",
                fontWeight: 600,
                fontSize: "1rem",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "#5568d3",
                  boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
                },
                "&:disabled": {
                  backgroundColor: "#E5E7EB",
                  color: "#9CA3AF",
                },
              }}
            >
              {loading ? "Processing..." : "Pay Now"}
            </CustomButton>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}
